import { CalendarClock, CalendarPlus, ExternalLink, Phone, PhoneCall, Repeat, StickyNote, User } from "lucide-react";
import * as React from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { BookCallbackDialog } from "@/features/book-callback";
import { CallbackBackupDialog } from "@/features/callback-backup";
import {
  callbackStatus, callbackWhen, canChangeBackup, forSkilled, howItIsCalled, whoTakesCalendarCallback,
} from "@/lib/callbacks";
import { useContact } from "@/lib/queries";
import type { CalendarCallback } from "@/lib/types";
import { formatPhoneNumber } from "@/lib/utils";

function Row({ icon: Icon, label, children }: { icon: typeof Phone; label: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-3">
      <Icon className="mt-0.5 size-4 shrink-0 text-text-muted" aria-hidden />
      <div className="min-w-0">
        <p className="text-xs text-text-muted">{label}</p>
        <div className="text-sm text-text">{children}</div>
      </div>
    </div>
  );
}

/**
 * A callback opened from the calendar (ACL-658): when, its backup times, who it is for, who takes it,
 * how it is called and why. Open record goes to the contact, Backup times opens the dialog the record
 * has, and Change time books it again through the booking form. There is no Cancel: an outcome ends a
 * callback (ADR-080).
 *
 * The contact's card, read when it opens, has their phone and their clock; a rep who cannot open the
 * contact still reads what the calendar knows, and has nothing to change.
 */
export function CallbackDetails({ callback, onClose }: { callback: CalendarCallback | null; onClose: () => void }) {
  const card = useContact(callback?.lead_id);
  const [doing, setDoing] = React.useState<"backups" | "time" | null>(null);
  React.useEffect(() => setDoing(null), [callback?.id]);
  const contact = card.data;
  const zone = contact ? (contact.clock_timezone ?? contact.timezone ?? null) : null;
  const still = callback !== null && (callback.status === "upcoming" || callback.status === "due");
  // The record's own open callback is what its backup dialog changes.
  const open = contact?.callback && contact.callback.id === callback?.id ? contact.callback : null;
  const when = (at: string) => {
    const { there, yours } = callbackWhen(at, zone);
    return yours ? `${there} (${yours})` : there;
  };

  return (
    <>
      <Dialog open={callback !== null && doing === null} onOpenChange={(isOpen) => !isOpen && onClose()}>
        <DialogContent variant="drawer">
          <DialogHeader>
            <DialogTitle>View details</DialogTitle>
          </DialogHeader>
          {callback && (
            <DialogBody className="space-y-5">
              <div className="space-y-2">
                <div className="flex flex-wrap gap-1.5">
                  <Badge tone="purple" size="sm">{forSkilled(callback) ? `${callback.only_skills.join(" and ")} callback` : "Callback"}</Badge>
                  <Badge tone={callbackStatus(callback, Date.now()).tone} size="sm">{callbackStatus(callback, Date.now()).label}</Badge>
                </div>
                <h2 className="text-lg font-semibold text-text">
                  {callback.purpose ?? "Callback"}
                  {callback.contact_name ? ` · ${callback.contact_name}` : ""}
                </h2>
              </div>
              <div className="space-y-4">
                <Row icon={CalendarClock} label="Callback time">{when(callback.due_at)}</Row>
                <Row icon={Repeat} label="If nobody answers">
                  {callback.backup_times.length === 0 ? (
                    "No backup time"
                  ) : (
                    <ol className="list-inside list-decimal">
                      {callback.backup_times.map((at) => (
                        <li key={at}>{when(at)}</li>
                      ))}
                    </ol>
                  )}
                </Row>
                <Row icon={User} label="Contact">
                  {callback.contact_name ?? "No name"}
                  {contact?.phone && <p className="text-xs text-text-muted">{formatPhoneNumber(contact.phone)}</p>}
                </Row>
                <Row icon={Phone} label="Who takes it">
                  {whoTakesCalendarCallback(callback)}
                  {callback.by && <p className="text-xs text-text-muted">Booked by {callback.by}</p>}
                </Row>
                {still && <Row icon={PhoneCall} label="How it is called">{howItIsCalled(callback.dialer)}</Row>}
                {callback.note && <Row icon={StickyNote} label="Note">{callback.note}</Row>}
              </div>
            </DialogBody>
          )}
          {callback && (
            <DialogFooter className="flex-wrap">
              <Button variant="outline" asChild>
                <Link to={`/contacts/${callback.lead_id}`}>
                  <ExternalLink />
                  Open record
                </Link>
              </Button>
              {still && open && canChangeBackup(open) && (
                <Button variant="outline" onClick={() => setDoing("backups")}>
                  <Repeat />
                  Backup times
                </Button>
              )}
              {still && contact && (
                <Button onClick={() => setDoing("time")}>
                  <CalendarPlus />
                  Change time
                </Button>
              )}
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
      {doing === "backups" && contact && open && <CallbackBackupDialog contact={contact} callback={open} onClose={onClose} />}
      {contact && callback && (
        <BookCallbackDialog
          open={doing === "time"}
          onOpenChange={(isOpen) => !isOpen && onClose()}
          contact={contact}
          changing={callback}
        />
      )}
    </>
  );
}
